import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { Box, Button, Grid, HStack, useToast } from "@chakra-ui/react";
import * as Yup from "yup";
import CustomField from "../../Common/Form/CustomField";
import { useHistory } from "react-router-dom";
import ProductsTable from "./ProductsTable";
import ContentCard from "../../Common/Layout/ContentCard";
import useFetch from "../../../hooks/useFetch";
import { Prompt } from "react-router";
import ConfirmationSwal from "../../../Utils/confirmationSwal";

const PriceListForm = ({ priceList, id }) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEdited, setIsEdited] = useState(false);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const { post, put } = useFetch();
  const history = useHistory();
  const toast = useToast();

  useEffect(() => {
    setProducts(priceList.products ? priceList.products : []);
    setIsLoading(false);
  }, []);

  const PriceListSchema = Yup.object().shape({
    name: Yup.string().required("Ingrese un nombre"),
  });

  const submitHandler = async (form, isRedirecting) => {
    await form.submitForm();
    if (Object.keys(await form.validateForm()).length !== 0) return false;

    const value = form.values;

    if (products.length === 0) {
      toast({
        title: "Se ha producido un error.",
        description: "Ingrese al menos un artículo.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });

      return false;
    }

    setIsSubmiting(true);
    let data = {
      name: value.name,
      description: value.description,
      products: products,
    };

    setIsEdited(false);
    if (isNaN(id)) {
      post("/priceLists", data)
        .then((res) => {
          toast({
            title: "Lista de precios creada con éxito.",
            description: "Se cargó la lista de precios correctamente.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });

          if (isRedirecting) {
            history.push("/lista-de-precios");
          } else {
            history.replace("/lista-de-precios");
            history.push(`/lista-de-precios/${res.id}`);
          }
        })
        .catch((e) => {
          toast({
            title: "Se ha producido un error.",
            description:
              e?.message || "Contacte con el administrador del sistema.",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsSubmiting(false);
        });
    } else {
      put(`/priceLists/${id}`, data)
        .then(() => {
          toast({
            title: "Lista de precios editada con éxito.",
            description: "Se editó la lista de precios correctamente.",
            status: "success",
            duration: 4000,
            isClosable: true,
          });
          isRedirecting && history.push("/lista-de-precios");
        })
        .catch((e) => {
          toast({
            title: "Se ha producido un error.",
            description:
              e?.message || "Contacte con el administrador del sistema.",
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsSubmiting(false);
        });
    }
  };

  const handleIsEdited = () => {
    setIsEdited(true);
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (isEdited) {
      setIsEdited(false);
      ConfirmationSwal.fire({
        title: "Parece que hay cambios no guardados",
        text: "Desea salir de la página?",
        icon: "question",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          history.push(nextLocation.pathname);
        }
      });
      return false;
    }
    return true;
  };

  return (
    <>
      <Box>
        <Formik
          initialValues={priceList}
          validationSchema={PriceListSchema}
          onSubmit={() => {}}
        >
          {(props) => (
            <Form>
              <ContentCard>
                <Grid
                  templateColumns={["repeat(1, 1fr)", "repeat(2, 1fr)"]}
                  gap={6}
                >
                  <CustomField
                    name="name"
                    title="Nombre"
                    placeholder="Ingrese un nombre"
                    props={props}
                    customStyle={{ textTransform: "uppercase" }}
                    setIsEdited={handleIsEdited}
                  ></CustomField>

                  <CustomField
                    name="description"
                    title="Descripción: (opcional)"
                    placeholder="Ingrese una descripción"
                    type="text"
                    inputType="textArea"
                    props={props}
                    customStyle={{ textTransform: "uppercase" }}
                    setIsEdited={handleIsEdited}
                  ></CustomField>
                </Grid>
              </ContentCard>
              <ContentCard>
                {!isLoading && (
                  <ProductsTable
                    purchaseId={id}
                    priceListProducts={products}
                    setProducts={setProducts}
                    setIsEdited={handleIsEdited}
                  ></ProductsTable>
                )}
              </ContentCard>
              <HStack
                justify={["center", "flex-end"]}
                mt="2rem"
                wrap={"wrap"}
                gap="0.5rem"
              >
                <Button variant="ghost" onClick={() => history.goBack()}>
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, true)}
                >
                  Guardar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, false)}
                >
                  Guardar y seguir editando
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </Box>
      <Prompt when={isEdited} message={(e) => handleBlockedNavigation(e)} />
    </>
  );
};

export default PriceListForm;
